import { DeleteModal } from '@/src/components/common/Modal/DeleteModal'
import LabResultDocumentPreview from '@/src/pages/LabTech/LabResultDocumentPreview'
import { src } from '@data'
import { useLab, useLabResult, useMarkNotificationForNotifiable, useUploadFile } from '@hook'
import { DeleteIcon, PrintIcon } from '@icons'
import { DOCTOR_MODAL_TYPE, useModalStore } from '@modal'
import { OrderStatus } from '@type/imaging.type'
import { LabDataMock, LabOrderData, LabOrderIncludedLabResult, LabOrderIncludedResource } from '@type/lab.type'
import { OrderLabTestIncluded } from '@type/order.type'
import { ActiveTab, Badge, Button, Loader, SideDrawer } from '@ui'
import ResultUploader from '@ui/FileUploader/results'
import { getError, getIncludedData, guardEncounterWrite, showToast } from '@util'
import { Avatar, Box, CheckBox, Text, Tip } from 'grommet'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { buildPlacedLines, createLabResultAttachmentPayload, orderTitleNumber } from './LabDetails.helpers'

const TABS = ['Order Details', 'Result'] as const

export type LabDetailsModalProps = {
  isOpen: boolean
  onClose: () => void
  id: string
  /** Row from the encounter table; used until `GET /lab_orders/:id` resolves (and for legacy `order` rows). */
  defaultData?: LabDataMock
}

const isLabOrderBundle = (row?: LabDataMock): row is LabOrderData => row?.type === 'lab_order'

const LabDetails = ({ isOpen, onClose, id, defaultData: rowFromTable }: LabDetailsModalProps) => {
  useMarkNotificationForNotifiable('LabOrder', isOpen ? id : null)
  const { openModal } = useModalStore()
  const { patientId = '', encounterId = '' } = useParams<{ patientId: string; encounterId: string }>()
  const {
    completeLabOrder,
    included: listIncluded,
    singleLabOrder,
    singleLabOrderIsLoading
  } = useLab({
    encounterId,
    id,
    patientId
  })

  const defaultData = useMemo((): LabDataMock | undefined => {
    const fromApi = singleLabOrder?.data
    if (fromApi?.type === 'lab_order') return fromApi
    return rowFromTable
  }, [rowFromTable, singleLabOrder?.data])

  const included = singleLabOrder?.included ?? listIncluded

  function isIncludedLabResult(x: LabOrderIncludedResource): x is LabOrderIncludedLabResult {
    return x.type === 'lab_result'
  }

  const labResults = useMemo(() => {
    if (!included?.length) return [] as LabOrderIncludedLabResult[]
    return included.filter(isIncludedLabResult)
  }, [included])

  const labResultListLabel = (r: LabOrderIncludedLabResult) => {
    const doc = r.attributes.document
    const base = doc?.name
      ? doc.name
      : r.attributes.attachment?.filename
        ? r.attributes.attachment.filename
        : r.attributes.created_at
          ? `Lab result · ${moment(r.attributes.created_at).format('MMM D, YYYY h:mm A')}`
          : `Lab result · #${r.id}`
    const testName = r.attributes.test_name?.trim()
    return testName ? `${base} (${testName})` : base
  }

  const [activeTab, setActiveTab] = useState<(typeof TABS)[number]>('Order Details')
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [printLabResult, setPrintLabResult] = useState<LabOrderIncludedLabResult | null>(null)
  const [isCompleting, setIsCompleting] = useState(false)
  const [resultToDelete, setResultToDelete] = useState<LabOrderIncludedLabResult | null>(null)
  const { uploadFile, isUploading } = useUploadFile()
  const { createLabResult, deleteLabResult, isDeletingLabResult } = useLabResult()

  useEffect(() => {
    setActiveTab('Order Details')
    setSelectedKeys(new Set())
  }, [id])

  const placedLines = useMemo(
    () => (defaultData ? buildPlacedLines(defaultData, included) : []),
    [defaultData, included]
  )
  const lineKeys = useMemo(() => placedLines.map(l => l.key), [placedLines])

  const staffRow = defaultData
    ? (getIncludedData(included, 'staff', defaultData.relationships.ordered_by.data?.id) as
        | { attributes?: { first_name?: string; last_name?: string }; meta?: { roles?: string[] } }
        | undefined)
    : undefined
  const orderableId =
    defaultData && !isLabOrderBundle(defaultData) ? defaultData.relationships.orderable.data?.id : undefined

  const statusLabel =
    defaultData === undefined
      ? '-'
      : isLabOrderBundle(defaultData)
        ? defaultData.attributes.status
        : defaultData.meta?.status || '-'

  const getLabTestIncluded = useCallback(
    (tid?: string) =>
      included?.find((item): item is OrderLabTestIncluded => item?.id === tid && item.type === 'lab_test'),
    [included]
  )

  const handlePrintResult = useCallback((result: LabOrderIncludedLabResult) => {
    if (!result.attributes.document) {
      showToast('No document available to print')
      return
    }
    setPrintLabResult(result)
    setTimeout(() => {
      window.print()
    }, 150)
  }, [])

  const detailRows = useMemo(() => {
    if (!defaultData) {
      return {
        'Order by': (
          <span className="ml-auto flex items-center gap-2">
            <Avatar size="24px" src={src} />
            <p className="text-black">-</p>
          </span>
        ),
        Comments: '-',
        Lab: '-',
        'Order date': '-',
        Test: '-'
      }
    }
    return {
      Comments: isLabOrderBundle(defaultData)
        ? defaultData.attributes.note || 'Nil'
        : defaultData.attributes.notes || 'Nil',
      Lab: isLabOrderBundle(defaultData) ? '-' : getLabTestIncluded(orderableId)?.attributes.category || '-',
      'Order by': (
        <span className="flex items-center gap-2">
          <Avatar size="24px" src={src} />
          <p className="text-black ">
            {staffRow?.attributes?.first_name
              ? `${staffRow.meta?.roles?.includes('doctor') ? 'Dr. ' : ''}${staffRow.attributes.first_name} ${staffRow.attributes.last_name ?? ''}`.trim()
              : '-'}
          </p>
        </span>
      ),
      'Order date': defaultData.attributes.created_at
        ? moment(defaultData.attributes.created_at).format('DD/MM/YYYY')
        : '-',
      Test: isLabOrderBundle(defaultData)
        ? defaultData.attributes.test_names?.join(', ') || '-'
        : getLabTestIncluded(orderableId)?.attributes.name || '-'
    }
  }, [defaultData, getLabTestIncluded, orderableId, staffRow])

  const allSelected = lineKeys.length > 0 && lineKeys.every(k => selectedKeys.has(k))

  // Custom / creatable tests with no order relationship — hide ones that already have an uploaded result.
  const uploadableTests = useMemo(() => {
    const uploadedNames = new Set(
      labResults.map(r => r.attributes.test_name?.trim().toLowerCase()).filter(Boolean) as string[]
    )
    return placedLines.filter(line => !line.orderId && !uploadedNames.has(line.label.trim().toLowerCase()))
  }, [labResults, placedLines])

  const toggleKey = (key: string, checked: boolean) => {
    setSelectedKeys(prev => {
      const next = new Set(prev)
      if (checked) next.add(key)
      else next.delete(key)
      return next
    })
  }

  const toggleAll = (checked: boolean) => {
    setSelectedKeys(checked ? new Set(lineKeys) : new Set())
  }

  const openCancelAll = () => {
    openModal(DOCTOR_MODAL_TYPE.CANCEL_ALL_LAB_ORDERS, { id })
  }

  const handleCompleteAllOrders = useCallback(async () => {
    if (!id) return
    if (!guardEncounterWrite()) return
    setIsCompleting(true)
    try {
      await completeLabOrder({ id })
      showToast('Lab order completed successfully')
    } catch (error: unknown) {
      const err = error as { response?: { data?: { errors?: { detail?: string }[] } } }
      showToast(err?.response?.data?.errors?.[0]?.detail || 'Failed to complete lab order')
    } finally {
      setIsCompleting(false)
    }
  }, [completeLabOrder, id])

  const isObjectEmpty = (obj: any) => {
    return obj && Object.keys(obj).length === 0 && obj.constructor === Object
  }

  const handleOpenResult = useCallback(
    (result: LabOrderIncludedLabResult) => {
      if (!isObjectEmpty(result.attributes.document)) {
        openModal(DOCTOR_MODAL_TYPE.LAB_RESULT_PREVIEW, { defaultData: result })
        return
      }

      const url = result.attributes.attachment?.url
      if (url) {
        window.open(url, '_blank', 'noopener')
        return
      }

      showToast('No preview available for this result.')
    },
    [openModal]
  )

  const handleUploadResult = useCallback(
    async (file: File, testName: string) => {
      try {
        const { signed_id } = await uploadFile(file)
        await createLabResult({
          formVal: createLabResultAttachmentPayload(signed_id, testName),
          lab_order_id: id
        })
        showToast('File uploaded and lab result created')
      } catch (err) {
        getError(err)
      }
    },
    [createLabResult, id, uploadFile]
  )

  const handleDeleteResult = useCallback(async (result: LabOrderIncludedLabResult) => {
    setResultToDelete(result)
  }, [])

  const confirmDeleteResult = useCallback(async () => {
    if (!resultToDelete) return
    try {
      await deleteLabResult(resultToDelete.id)
      showToast('Lab result deleted successfully')
      setResultToDelete(null)
    } catch (err) {
      getError(err)
    }
  }, [deleteLabResult, resultToDelete])

  if (!isOpen) return null

  if (!defaultData) {
    return (
      <SideDrawer onClose={onClose} title="Details">
        <Box align="center" justify="center" pad="large">
          {singleLabOrderIsLoading ? <Loader /> : <Text color="#666">Unable to load this lab order.</Text>}
        </Box>
      </SideDrawer>
    )
  }

  return (
    <SideDrawer onClose={onClose} title="Details">
      <div className="px-6 pt-2">
        <div className="flex items-center justify-between mb-2">
          <Text color="black" size="22px" weight="bold">
            Order {orderTitleNumber(id)}
          </Text>
          <Badge
            justify="center"
            label={statusLabel === 'in_progress' ? 'In Progress' : statusLabel}
            size="small"
            status={statusLabel as keyof typeof OrderStatus}
          />
        </div>
        {/* <Text color="#666666" margin={{ bottom: '16px' }} size="14px">
          {summarySubtitle(defaultData, included)}
        </Text> */}
        <div className="flex flex-wrap gap-2 mb-5">
          <Button
            disabled={
              isCompleting || statusLabel.toLowerCase() === 'completed' || statusLabel.toLowerCase() === 'cancelled'
            }
            icon={<PrintIcon />}
            kinds="outline"
            label={isCompleting ? 'Completing…' : 'Complete all orders'}
            onClick={handleCompleteAllOrders}
            rounded={false}
            size={'sm'}
            type="button"
          />
          <Button
            className="!rounded-[10px] bg-white !text-[#E34949] hover:bg-[#FFF5F5]"
            disabled={placedLines.every(
              line => line.lineStatus.toLowerCase() === 'cancelled' || line.lineStatus.toLowerCase() === 'completed'
            )}
            icon={<DeleteIcon alt />}
            kinds="outline"
            label="Cancel all orders"
            onClick={openCancelAll}
            rounded={false}
            size={'sm'}
            type="button"
          />
        </div>
      </div>

      <ActiveTab divider key={id} options={[...TABS]} setActive={v => setActiveTab(v as (typeof TABS)[number])} />

      <div className="flex flex-col gap-4 pb-8 pt-4 px-6">
        {activeTab === 'Order Details' && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3">
              {Object.entries(detailRows).map(([key, value]) => (
                <div className="flex items-center justify-between gap-4" key={key}>
                  <p className="text-[#666666] text-sm font-medium">{key}:</p>
                  <div className="flex justify-end">
                    <Tip
                      content={value}
                      dropProps={{ style: { fontSize: '14px', lineHeight: '24px', maxWidth: '400px' } }}
                    >
                      <p className="text-black text-end truncate max-w-[350px]">{value}</p>
                    </Tip>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-2">
              <p className="text-black text-base mb-2">Orders placed</p>
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between py-3">
                  <CheckBox
                    checked={allSelected}
                    disabled={placedLines.every(
                      line =>
                        line.lineStatus.toLowerCase() === 'cancelled' || line.lineStatus.toLowerCase() === 'completed'
                    )}
                    label={<p className="text-sm">All Tests</p>}
                    onChange={e => {
                      toggleAll(e.target.checked)
                    }}
                  />
                  {allSelected && (
                    <Button
                      className="!min-w-0 !rounded-lg border border-[#E34949] !bg-transparent !px-3 !py-1 !text-[13px] !font-medium !text-[#E34949]"
                      icon={<DeleteIcon alt />}
                      kinds="normal"
                      label="Cancel all orders"
                      onClick={openCancelAll}
                      size="auto"
                      type="button"
                    />
                  )}
                </div>
                {placedLines.map(line => {
                  const cancelled = line.lineStatus.toLowerCase() === 'cancelled'
                  const completed = line.lineStatus.toLowerCase() === 'completed'
                  const canCancelLine = Boolean(line.orderId)
                  return (
                    <div className="flex items-center justify-between py-1" key={line.key}>
                      <CheckBox
                        checked={selectedKeys.has(line.key)}
                        disabled={cancelled || completed || !canCancelLine}
                        label={<p className="text-sm">{line.label}</p>}
                        onChange={e => toggleKey(line.key, e.target.checked)}
                      />
                      {cancelled ? (
                        <Badge
                          label="Cancelled"
                          size="small"
                          status="cancelled"
                          style={{ background: '#FEECEC', color: '#E34949', minWidth: 'auto' }}
                        />
                      ) : (
                        <Button
                          className="!min-w-0 !rounded-lg border border-[#E34949] !bg-transparent !px-3 !py-1 !text-[13px] !font-medium !text-[#E34949]"
                          disabled={completed || cancelled || !canCancelLine}
                          icon={<DeleteIcon alt />}
                          kinds="normal"
                          label="Cancel"
                          onClick={() => {
                            if (!line.orderId) {
                              showToast('This test cannot be cancelled individually yet.')
                              return
                            }
                            openModal(DOCTOR_MODAL_TYPE.CANCEL_LAB_ORDER, { id: line.orderId })
                          }}
                          size="auto"
                          type="button"
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'Result' && (
          <div className="flex flex-col gap-2">
            {labResults.length > 0 ? (
              <div className="mb-2 flex flex-col gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-[#666]">Saved results</p>
                {labResults.map(r => (
                  <div
                    className="flex w-full items-center gap-2 rounded-md border border-[#e8e8e8] bg-white px-2 py-2 text-sm transition hover:border-[#ccc] hover:bg-[#fafafa]"
                    key={r.id}
                  >
                    <button
                      className="min-w-0 flex-1 truncate px-1 py-1 text-left font-medium text-[#222]"
                      onClick={() => handleOpenResult(r)}
                      type="button"
                    >
                      {labResultListLabel(r)}
                    </button>
                    {!isObjectEmpty(r.attributes.document) ? (
                      <button
                        className="shrink-0 rounded px-2 py-1 text-xs font-medium text-[#1565C0] hover:bg-[#E3F2FD]"
                        onClick={() => handlePrintResult(r)}
                        type="button"
                      >
                        Print
                      </button>
                    ) : null}
                    <button
                      className="shrink-0 rounded px-2 py-1 text-xs text-[#666] hover:bg-[#f0f0f0]"
                      onClick={() => handleOpenResult(r)}
                      type="button"
                    >
                      Preview
                    </button>
                    {statusLabel.toLowerCase() !== 'completed' && statusLabel.toLowerCase() !== 'cancelled' && (
                      <button
                        className="shrink-0 rounded px-2 py-1 text-xs font-medium text-[#E34949] hover:bg-[#FEECEC]"
                        disabled={isDeletingLabResult}
                        onClick={() => handleDeleteResult(r)}
                        type="button"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <Text color="#909090" size="14px">
                No results available.
              </Text>
            )}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-[#666] mb-2">Other tests</p>
              {uploadableTests.length > 0 ? (
                <div className="flex flex-col gap-3">
                  {uploadableTests.map(line => (
                    <ResultUploader
                      isLoading={isUploading}
                      key={line.key}
                      label={line.label}
                      onFileUpload={file => handleUploadResult(file, line.label)}
                    />
                  ))}
                </div>
              ) : (
                <Text color="#909090" size="14px">
                  No other tests.
                </Text>
              )}
            </div>
          </div>
        )}
      </div>
      {printLabResult?.attributes.document ? (
        <div className="hidden print:block" id="lab-result-print">
          <div className="lab-result-print-content bg-white p-8 text-[#111]">
            <LabResultDocumentPreview document={printLabResult.attributes.document} />
          </div>
        </div>
      ) : null}

      {resultToDelete && (
        <DeleteModal
          description={`Are you sure you want to delete "${labResultListLabel(resultToDelete)}"? This action cannot be undone.`}
          handleSubmit={confirmDeleteResult}
          isLoading={isDeletingLabResult}
          lBtnLabel="Cancel"
          onClose={() => setResultToDelete(null)}
          rBtnLabel="Yes, Delete"
          title="Delete Lab Result"
        />
      )}

      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            #lab-result-print,
            #lab-result-print * {
              visibility: visible;
            }
            #lab-result-print {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }
            #lab-result-print .lab-result-document-preview,
            #lab-result-print .lab-result-document-preview * {
              max-height: none !important;
              overflow: visible !important;
            }
          }
        `}
      </style>
    </SideDrawer>
  )
}

export default LabDetails
